/**
 * useUnsavedEditGuard — register an editor with the global unsaved-changes guard.
 *
 * The hook snapshots one or more localStorage keys on mount. The editor
 * continues to auto-save into those keys as the user makes changes (so the
 * editor's own preview stays in sync). When the caregiver tries to navigate
 * away through any layout-controlled exit (back button, hamburger menu),
 * the global provider compares snapshot vs. current and shows a confirm
 * dialog with Save / Discard / Keep editing.
 *
 * - save():    accept current state — snapshot is updated to the live values
 *              so the next isDirty() comparison returns false.
 * - discard(): revert each tracked key in localStorage back to its snapshot
 *              (or remove it if it didn't exist), then call onAfterRevert so
 *              the editor's React state can re-load from the restored values.
 *
 * Editors should call `markSaved()` from the "Done editing" / explicit save
 * button before navigating, so the dialog never appears on the happy path.
 */
import { useCallback, useEffect, useRef } from "react";
import { useUnsavedChanges } from "@/contexts/UnsavedChangesContext";

interface Options {
  storageKeys: string[];
  onAfterRevert?: () => void;
}

export function useUnsavedEditGuard({ storageKeys, onAfterRevert }: Options) {
  const ctx = useUnsavedChanges();
  const snapshotRef = useRef<Record<string, string | null>>({});
  const onAfterRevertRef = useRef(onAfterRevert);
  onAfterRevertRef.current = onAfterRevert;

  // Take baseline snapshot once per editor mount.
  useEffect(() => {
    const snap: Record<string, string | null> = {};
    for (const k of storageKeys) snap[k] = localStorage.getItem(k);
    snapshotRef.current = snap;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const markSaved = useCallback(() => {
    const snap: Record<string, string | null> = {};
    for (const k of storageKeys) snap[k] = localStorage.getItem(k);
    snapshotRef.current = snap;
  }, [storageKeys]);

  const isDirty = useCallback(() => {
    const snap = snapshotRef.current;
    for (const k of storageKeys) {
      if (localStorage.getItem(k) !== (snap[k] ?? null)) return true;
    }
    return false;
  }, [storageKeys]);

  const discard = useCallback(() => {
    const snap = snapshotRef.current;
    for (const k of storageKeys) {
      const v = snap[k];
      if (v === null || v === undefined) localStorage.removeItem(k);
      else localStorage.setItem(k, v);
    }
    onAfterRevertRef.current?.();
  }, [storageKeys]);

  // Register/unregister with provider. Re-register when callbacks change.
  useEffect(() => {
    if (!ctx) return;
    const guard = { isDirty, save: markSaved, discard };
    ctx.registerGuard(guard);
    return () => ctx.unregisterGuard(guard);
  }, [ctx, isDirty, markSaved, discard]);

  return { markSaved, isDirty };
}
